import { Sound } from '@/classes';
import { keyCodes } from '@/constants/keyCode';
import { useExtendFocusable } from '@/hooks/useExtendFocusable';
import { useFocusElement } from '@/hooks/useFocusElement';
import { useRouter } from '@/hooks/useRouter';
import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

interface CardProps {
  title: string | React.ReactNode;
  navigateTo: string;
  icon: React.ReactNode;
  onClick?: () => void;
}

export function MyPageCard({ title, navigateTo, icon, onClick }: CardProps) {
  const router = useRouter();
  const [searchParams] = useSearchParams();
  const { ref, focused, focusKey, focusSelf } = useExtendFocusable({ focusKey: navigateTo });

  const handleNavigate = useCallback(() => {
    Sound.press();
    if (onClick) {
      onClick();
    } else {
      router.push(navigateTo, { focusKey });
    }
  }, [onClick, router, navigateTo, focusKey]);

  const handleClick = useCallback(() => {
    focusSelf();
    handleNavigate();
  }, [focusSelf, handleNavigate]);

  useEffect(() => {
    const targetFocusKey = searchParams.get('f');
    if (focusKey === targetFocusKey) {
      focusSelf();
    }
  }, [focusKey, focusSelf, searchParams]);

  useFocusElement({ ref, focused, onClick: handleNavigate, keyCode: keyCodes.ENTER });

  return (
    <div
      tabIndex={0}
      ref={ref}
      className={`relative p-[26px] w-[365px] h-[272px] bg-white overflow-hidden rounded-[15px] ease-in-out duration-300 outline-disable outline-primary-12`}
      style={{
        margin: '0 0.5rem',
        border: '1px solid #E1E1E1',
      }}
      onClick={handleClick}
      onContextMenu={handleClick}
    >
      <div className="flex justify-between flex-col w-full h-full">
        {icon}
        {typeof title === 'string' ? (
          <p className="text-[36px] font-bold text-black flex items-center h-[84px] leading-10">{title}</p>
        ) : (
          title
        )}
      </div>
    </div>
  );
}
